import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/shared/ui/button";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { handleError } from "@/shared/lib/errors/handleError";

interface Props {
  children: ReactNode;
  /** Human label for logs, e.g. "AdminDashboard". */
  routeName: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  resetKey: number;
}

/**
 * Page-level error boundary. Wraps an individual route element so that a
 * crash inside one screen never blanks the entire shell — the user can
 * recover or navigate away without losing their session.
 *
 * Differs from:
 * - `ErrorBoundary` (root): last-resort fallback wrapping the whole app.
 * - `SectionErrorBoundary`: wraps a widget inside a page.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    handleError(error, {
      context: `Route:${this.props.routeName}`,
      silent: true,
      extra: { componentStack: info.componentStack },
    });
  }

  handleRetry = () => {
    this.setState((prev) => ({ hasError: false, error: undefined, resetKey: prev.resetKey + 1 }));
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-4">
          <div className="max-w-md w-full border-4 border-foreground bg-card shadow-neo p-8 text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/20 border-4 border-destructive">
              <AlertTriangle className="w-8 h-8 text-destructive" strokeWidth={3} />
            </div>
            <div className="space-y-2">
              <h1 className="font-heavy text-2xl uppercase text-foreground">
                This page hit a snag
              </h1>
              <p className="text-muted-foreground font-body text-sm">
                The rest of the app is fine — you can retry or head back to safety.
              </p>
            </div>
            {this.state.error && import.meta.env.DEV && (
              <div className="bg-muted border-2 border-border rounded p-3 text-left">
                <p className="text-xs font-mono text-muted-foreground break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="border-2 border-foreground shadow-neo font-heavy uppercase"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Button
                onClick={this.handleRetry}
                className="border-2 border-foreground shadow-neo font-heavy uppercase bg-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}
